/*
Purpose: Announcement list with Bulk Email Send, Status View, and Per-Parent Resend
Developer: Integrated Feature
Project: TCMS
*/

import React from 'react';
import { useState, useEffect, useRef } from "react";
import { Row, Col, Form, Modal, Button, Badge, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { useFormik } from "formik";
import * as yup from "yup";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { security_key, BASE_URL } from "../global";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { Table } from "react-bootstrap";
import Moment from 'moment';

const Announcement = () => {

  const [data, setData] = useState([]);

  const [modalShowAdd, setModalShowAdd] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);
  const [showDelete, setDeleteShow] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [isDeleteSubmitting, setIsDeleteSubmitting] = useState(false);

  // ── Email Status Modal ─────────────────────────────────────────────────────
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusModalData, setStatusModalData] = useState(null);   // { announcement, parents, summary }
  const [statusLoading, setStatusLoading] = useState(false);
  const [sendingBulk, setSendingBulk] = useState(null);          // bulk send in progress
  // const [sendingBulkKey, setSendingBulkKey] = useState(null);
  const [resendingKey, setResendingKey] = useState(null);         // pk_par_key being resent

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.post(BASE_URL + 'API/api_index.php', {
        fk_cent_key: sessionStorage.getItem('key'),
        security_key: security_key,
        mode: "getCentreAnnouncements"
      });
      if (response.data.success === true) {
        setData(response.data.data);
      } else {
        console.error('Error fetching data.');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // ── Bulk Send ──────────────────────────────────────────────────────────────
  const handleSendBulk = async (announcement) => {
    setSendingBulk(announcement.pk_announce_key);
    // setSendingBulkKey(announcement.pk_announce_key);
    try {
      const response = await axios.post(BASE_URL + 'API/api_index.php', {
        security_key: security_key,
        mode: "triggerAnnouncementBulkEmail",
        fk_announce_key: announcement.pk_announce_key,
        fk_cent_key: sessionStorage.getItem('key'),
      });

      const res = response.data?.data;
      if (response.data?.success && res?.status === 'success') {
        toast.success(
          `Emails sent! ✓ ${res.sent} sent, ${res.failed} failed across ${res.batches} batch(es).`,
          { autoClose: 4000 }
        );
      } else if (res?.status === 'info') {
        toast.info(res.message || 'All parents already processed.', { autoClose: 700 });
      } else {
        toast.error('Failed to send bulk email. Please try again.', { autoClose: 700 });
      }
    } catch (error) {
      console.error('Bulk send error:', error);
      toast.error('Failed to send bulk email. Please try again.', { autoClose: 700 });
    } finally {
      // setSendingBulk(false);
      setSendingBulk(null);

    }
  };

  // ── View Status Modal ──────────────────────────────────────────────────────
  const handleViewStatus = async (announcement) => {
    setStatusModalData({ announcement, parents: [], summary: {} });
    setShowStatusModal(true);
    setStatusLoading(true);
    try {
      const response = await axios.post(BASE_URL + 'API/api_index.php', {
        security_key: security_key,
        mode: "getAnnouncementEmailStatus",
        fk_announce_key: announcement.pk_announce_key,
        fk_cent_key: sessionStorage.getItem('key'),
      });
      if (response.data?.success && response.data?.data?.status === 'success') {
        const d = response.data.data;
        setStatusModalData({ announcement, parents: d.parents, summary: d.summary });
      } else {
        toast.error('Failed to fetch email status.', { autoClose: 700 });
        setShowStatusModal(false);
      }
    } catch (error) {
      console.error('Status fetch error:', error);
      toast.error('Failed to fetch email status.', { autoClose: 700 });
      setShowStatusModal(false);
    } finally {
      setStatusLoading(false);
    }
  };

  // ── Resend to single parent ────────────────────────────────────────────────
  const handleResendToParent = async (parent) => {
    setResendingKey(parent.pk_par_key);
    try {
      const response = await axios.post(BASE_URL + 'API/api_index.php', {
        security_key: security_key,
        mode: "resendAnnouncementEmailToParent",
        fk_announce_key: statusModalData.announcement.pk_announce_key,
        fk_cent_key: sessionStorage.getItem('key'),
        fk_par_key: parent.pk_par_key,
      });
      const res = response.data?.data;
      if (response.data?.success && res?.status === 'success') {
        toast.success(`Email sent to ${parent.par_emailid}`, { autoClose: 700 });
        // Refresh status list
        await handleViewStatus(statusModalData.announcement);
      } else {
        toast.error(res?.message || 'Failed to resend. Try again.', { autoClose: 700 });
      }
    } catch (error) {
      console.error('Resend error:', error);
      toast.error('Failed to resend. Try again.', { autoClose: 700 });
    } finally {
      setResendingKey(null);
    }
  };

  // ── Status badge helper ────────────────────────────────────────────────────
  const getStatusBadge = (status) => {
    const map = {
      sent:          { bg: 'success',   label: 'Sent' },
      pending:       { bg: 'warning',   label: 'Pending' },
      failed:        { bg: 'danger',    label: 'Failed' },
      not_initiated: { bg: 'secondary', label: 'Not Initiated' },
    };
    const cfg = map[status] || map['not_initiated'];
    return <Badge bg={cfg.bg}>{cfg.label}</Badge>;
  };

  // ── Formik / Yup ───────────────────────────────────────────────────────────
  const today = dayjs().startOf("day");
  const basicSchema = yup.object().shape({
    start_date: yup.string()
      .required("Start date is required.")
      .test("valid-date", "Invalid start date.", value => dayjs(value, 'YYYY-MM-DD', true).isValid())
      .test("min-date-check", "Start date cannot be in the past.", function (value) {
        if (!value) return false;
        const date = dayjs(value, 'YYYY-MM-DD', true);
        if (!date.isValid()) return false;
        if (isEditMode) return true;
        return date.isSame(today, "day") || date.isAfter(today);
      }),
    end_date: yup.string()
      .required("End date is required.")
      .test("valid-date", "Invalid end date.", value => dayjs(value, 'YYYY-MM-DD', true).isValid())
      .test("end-after-start", "End date cannot be before start date.", function (value) {
        const { start_date } = this.parent;
        if (!value || !start_date) return false;
        const end = dayjs(value, 'YYYY-MM-DD', true);
        const start = dayjs(start_date, 'YYYY-MM-DD', true);
        if (!end.isValid() || !start.isValid()) return false;
        return end.isSame(start, "day") || end.isAfter(start);
      }),
    message: yup.string()
      .transform(value => (value ? value.trim() : ""))
      .required("Message is required.")
      .min(2, "Minimum 2 characters required."),
  });

  const initialValues = {
    start_date: null,
    end_date: null,
    message: '',
    fk_cent_key: sessionStorage.getItem('key'),
  };

  const formik = useFormik({
    initialValues,
    validationSchema: basicSchema,
    enableReinitialize: true,
    onSubmit: async (formValues) => {
      setIsSubmitting(true);
      const payload = { ...formValues };
      try {
        const response = await axios.post(BASE_URL + "API/api_index.php", {
          security_key: security_key,
          mode: isEditMode ? "updateAnnouncement" : "addAnnouncement",
          pk_announce_key: isEditMode ? selectedData.pk_announce_key : null,
          ...payload,
        });
        if (response.data?.success === true && response.data?.data === 1) {
          toast.success(
            isEditMode ? "Announcement updated successfully." : "The announcement has been added successfully.",
            { autoClose: 700 }
          );
          fetchData();
          setModalShowAdd(false);
          formik.resetForm();
          setIsEditMode(false);
          setSelectedData(null);
        } else {
          toast.error(
            isEditMode ? "Failed to update the announcement. Please try again later." : "Failed to add the announcement. Please try again later.",
            { autoClose: 700 }
          );
        }
      } catch (error) {
        console.error("Save error:", error);
        toast.error(
          isEditMode ? "Failed to update the announcement. Please try again later." : "Failed to add the announcement. Please try again later.",
          { autoClose: 700 }
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  const { values, handleBlur, touched, handleChange, errors, handleSubmit, setTouched } = formik;

  const handleDeleteClick = (row) => { setSelectedData({ ...row }); setDeleteShow(true); };
  const handleEditClick = (row) => {
    setSelectedData({ ...row });
    formik.setValues({
      start_date: row.announce_start_date || null,
      end_date: row.announce_end_date || null,
      message: row.announce_message || "",
      fk_cent_key: row.fk_cent_key || sessionStorage.getItem("key"),
    });
    setIsEditMode(true);
    setModalShowAdd(true);
  };
  const handleAddClick = () => {
    formik.resetForm();
    setSelectedData(null);
    setIsEditMode(false);
    setModalShowAdd(true);
  };

  function onSubmitDeleteAnnouncement() {
    setIsDeleteSubmitting(true);
    axios.post(BASE_URL + "API/api_index.php", {
      security_key: security_key,
      fk_cent_key: sessionStorage.getItem('key'),
      mode: "deleteAnnouncement",
      pk_announce_key: selectedData?.pk_announce_key
    }).then((responsedelete) => {
      if (responsedelete.data.data == 1) {
        setDeleteShow(false);
        toast.success("Announcement deleted successfully.", { autoClose: 700 });
        fetchData();
      } else {
        toast.error("Unable to delete the announcement. Please try again.", { autoClose: 700 });
      }
      setIsDeleteSubmitting(false);
    }).catch(() => {
      toast.error("Unable to delete the announcement. Please try again.", { autoClose: 700 });
      setIsDeleteSubmitting(false);
    });
  }

  // ── Summary pill helper ────────────────────────────────────────────────────
  const SummaryPill = ({ label, count, color }) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      padding: '4px 12px', borderRadius: '20px', fontSize: '13px',
      fontWeight: 600, background: color + '18', color: color, border: `1px solid ${color}40`
    }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block'
      }} />
      {count} {label}
    </span>
  );

  return (
    <>
      <ToastContainer position="top-center" />

      {/* ── Delete Modal ────────────────────────────────────────────────────── */}
      <Modal show={showDelete} variant="danger">
        <Modal.Header>Announcement</Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this announcement?</p>
        </Modal.Body>
        <Modal.Footer className='pt-0'>
          <div className="mx-auto d-flex gap-2">
            <Button variant="contained" className="primary-gray" onClick={() => setDeleteShow(false)}>Cancel</Button>
            <Button variant="contained" type='submit' className="primary-filled" disabled={isDeleteSubmitting} onClick={() => onSubmitDeleteAnnouncement()}>Yes</Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* ── Add / Edit Modal ────────────────────────────────────────────────── */}
      <Modal
        show={modalShowAdd}
        onHide={() => { setModalShowAdd(false); setSelectedData(null); }}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Form autoComplete="off" onSubmit={handleSubmit} ref={formRef}>
          <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">
              <h5 className='page-title'>
                {isEditMode ? "Edit Announcement" : "Add Announcement"}
              </h5>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col lg="6" md="6" sm="12" className='position-relative'>
                <h6 className="form-lable required-field">Start Date</h6>
                <LocalizationProvider className={'form-control'} dateAdapter={AdapterDayjs}>
                  <DatePicker
                    views={['year', 'month', 'day']}
                    value={dayjs(values.start_date, 'YYYY-MM-DD')}
                    onChange={(date) => {
                      const event = { target: { name: "start_date", value: date != null ? dayjs(date, 'YYYY-MM-DD').format('YYYY-MM-DD') : date } };
                      handleChange(event);
                    }}
                    onBlur={(event) => { handleBlur(event); setTouched({ ...touched, start_date: true }); }}
                    name="start_date"
                    slotProps={{ textField: { readOnly: true, size: 'small', id: 'start_date' } }}
                    className={errors.start_date && touched.start_date ? "input-error w-100" : "w-100"}
                    minDate={isEditMode
                      ? (selectedData?.announce_start_date
                        ? dayjs().isAfter(dayjs(selectedData.announce_start_date), "day")
                          ? dayjs(selectedData.announce_start_date) : dayjs()
                        : dayjs())
                      : dayjs()}
                    format="DD-MM-YYYY"
                  />
                </LocalizationProvider>
                {errors.start_date && touched.start_date && <span className="error">{errors.start_date}</span>}
              </Col>
              <Col lg="6" md="6" sm="12" className='position-relative'>
                <h6 className="form-lable required-field">End Date</h6>
                <LocalizationProvider className={'form-control'} dateAdapter={AdapterDayjs}>
                  <DatePicker
                    views={['year', 'month', 'day']}
                    value={dayjs(values.end_date, 'YYYY-MM-DD')}
                    onChange={(date) => {
                      const event = { target: { name: "end_date", value: date != null ? dayjs(date, 'YYYY-MM-DD').format('YYYY-MM-DD') : date } };
                      handleChange(event);
                    }}
                    onBlur={(event) => { handleBlur(event); setTouched({ ...touched, end_date: true }); }}
                    name="end_date"
                    slotProps={{ textField: { readOnly: true, size: 'small', id: 'end_date' } }}
                    className={errors.end_date && touched.end_date ? "input-error w-100" : "w-100"}
                    minDate={dayjs(values.start_date)}
                    format="DD-MM-YYYY"
                  />
                </LocalizationProvider>
                {errors.end_date && touched.end_date && <span className="error">{errors.end_date}</span>}
              </Col>
              <Col lg="12" md="12" sm="12" className='position-relative mt-2'>
                <h6 className="form-lable required-field">Message</h6>
                <Form.Control
                  as="textarea" rows={4}
                  name="message" id="message"
                  value={values.message}
                  onChange={handleChange} onBlur={handleBlur}
                  placeholder="Enter your announcement message..."
                  className={errors.message && touched.message ? "input-error" : ""}
                />
                {errors.message && touched.message && <p className="error">{errors.message}</p>}
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <div className="mx-auto d-flex gap-2">
              <Button variant="contained" className="primary-gray" onClick={() => setModalShowAdd(false)}>Cancel</Button>
              <Button variant="contained" type='submit' className="primary-filled" disabled={isSubmitting}>Save</Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ── Email Status Modal ───────────────────────────────────────────────── */}
      <Modal
        show={showStatusModal}
        onHide={() => { setShowStatusModal(false); setStatusModalData(null); }}
        size="xl"
        centered
        scrollable
      >
        <Modal.Header closeButton style={{ borderBottom: '2px solid #f0f0f0' }}>
          <Modal.Title>
            <div className="d-flex align-items-center gap-2">
              <span style={{
                width: 32, height: 32, borderRadius: 8,
                background: '#4A90D915', display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center', fontSize: 16
              }}>📧</span>
              <div>
                <h5 className='page-title mb-0' style={{ fontSize: 16 }}>Email Status</h5>
                {statusModalData?.announcement?.announce_message && (
                  <small style={{ color: '#888', fontWeight: 400, fontSize: 12 }}>
                    {statusModalData.announcement.announce_message.length > 80
                      ? statusModalData.announcement.announce_message.substring(0, 80) + '...'
                      : statusModalData.announcement.announce_message}
                  </small>
                )}
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: '20px 24px' }}>
          {statusLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
              <div className="text-center">
                <Spinner animation="border" variant="primary" style={{ width: 40, height: 40 }} />
                <p className="mt-3" style={{ color: '#888', fontSize: 14 }}>Fetching email status...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Summary pills */}
              {statusModalData?.summary && (
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <SummaryPill label="Sent"          count={statusModalData.summary.sent          || 0} color="#28a745" />
                  <SummaryPill label="Pending"       count={statusModalData.summary.pending       || 0} color="#ffc107" />
                  <SummaryPill label="Failed"        count={statusModalData.summary.failed        || 0} color="#dc3545" />
                  <SummaryPill label="Not Initiated" count={statusModalData.summary.not_initiated || 0} color="#6c757d" />
                </div>
              )}

              {/* Status Table */}
              <div style={{ overflowX: 'auto' }}>
                <Table bordered hover size="sm" style={{ fontSize: 13, marginBottom: 0 }}>
                  <thead style={{ background: '#f8f9fa' }}>
                    <tr>
                      <th style={{ width: '5%' }}>#</th>
                      <th style={{ width: '22%' }}>Parent Name</th>
                      <th style={{ width: '28%' }}>Email</th>
                      <th style={{ width: '15%' }}>Status</th>
                      <th style={{ width: '18%' }}>Sent At</th>
                      <th style={{ width: '12%', textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statusModalData?.parents?.length > 0 ? (
                      statusModalData.parents.map((parent, index) => (
                        <tr key={parent.pk_par_key}
                          style={{
                            background: parent.mail_status === 'failed' ? '#fff5f5' :
                              parent.mail_status === 'sent' ? '#f6fff8' : 'white'
                          }}
                        >
                          <td style={{ verticalAlign: 'middle', color: '#888' }}>{index + 1}</td>
                          <td style={{ verticalAlign: 'middle', fontWeight: 500 }}>
                            {parent.par_firstname} {parent.par_surname}
                          </td>
                          <td style={{ verticalAlign: 'middle', color: '#555' }}>
                            {parent.par_emailid}
                          </td>
                          <td style={{ verticalAlign: 'middle' }}>
                            {getStatusBadge(parent.mail_status)}
                          </td>
                          <td style={{ verticalAlign: 'middle', color: '#777', fontSize: 12 }}>
                            {parent.sent_at
                              ? Moment(parent.sent_at).format('DD-MM-YYYY hh:mm A')
                              : <span style={{ color: '#ccc' }}>—</span>}
                          </td>
                          <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                            {/* Show Send button only for non-sent parents */}
                            {parent.mail_status !== 'sent' ? (
                              <Button
                                size="sm"
                                variant="outline-primary"
                                disabled={resendingKey === parent.pk_par_key}
                                onClick={() => handleResendToParent(parent)}
                                style={{ fontSize: 11, padding: '2px 10px', whiteSpace: 'nowrap' }}
                              >
                                {resendingKey === parent.pk_par_key ? (
                                  <><Spinner animation="border" size="sm" /> Sending...</>
                                ) : (
                                  'Send'
                                )}
                              </Button>
                            ) : (
                              <span style={{ color: '#28a745', fontSize: 12 }}>✓ Done</span>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', color: '#aaa', padding: '30px' }}>
                          No parents found for this centre.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Error details for failed rows */}
              {statusModalData?.parents?.some(p => p.mail_status === 'failed' && p.error_message) && (
                <div className="mt-3">
                  <small style={{ color: '#dc3545', fontWeight: 600 }}>⚠ Failed email errors:</small>
                  {statusModalData.parents
                    .filter(p => p.mail_status === 'failed' && p.error_message)
                    .map(p => (
                      <div key={p.pk_par_key} style={{
                        fontSize: 11, color: '#dc3545', background: '#fff5f5',
                        border: '1px solid #fecdcd', borderRadius: 4,
                        padding: '4px 10px', marginTop: 4
                      }}>
                        <strong>{p.par_emailid}</strong>: {p.error_message}
                      </div>
                    ))
                  }
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer style={{ borderTop: '2px solid #f0f0f0' }}>
          <small style={{ color: '#aaa', fontSize: 11, marginRight: 'auto' }}>
            {statusModalData?.parents?.length || 0} parent(s) listed · Sorted by priority (failed first)
          </small>
          <Button variant="secondary" size="sm" onClick={() => { setShowStatusModal(false); setStatusModalData(null); }}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Main Page ───────────────────────────────────────────────────────── */}
      <div>
        <>
          <Row className="mt-2 align-items-center mb-3">
            <Col xs={12} className="d-flex justify-content-end">
              <Button onClick={() => handleAddClick()} variant="primary">
                ADD
              </Button>
            </Col>
          </Row>

          <Table bordered hover>
            <thead>
              <tr>
                <th style={{ width: "40%" }}>Announcement Message</th>
                <th style={{ width: "13%" }}>Start Date</th>
                <th style={{ width: "13%" }}>End Date</th>
                <th style={{ width: "34%" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                data.map((message, index) => (
                  <tr key={index}>
                    <td>{message.announce_message}</td>
                    <td>
                      {message?.announce_start_date
                        ? Moment(message.announce_start_date, 'YYYY-MM-DD').format('DD-MM-YYYY')
                        : '-'}
                    </td>
                    <td>
                      {message?.announce_end_date
                        ? Moment(message.announce_end_date, 'YYYY-MM-DD').format('DD-MM-YYYY')
                        : '-'}
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-2">
                        <Button
                          onClick={() => handleEditClick(message)}
                          variant="primary"
                          size="sm"
                        >
                          Edit
                        </Button>
                        <Button
                          onClick={() => handleDeleteClick(message)}
                          variant="danger"
                          size="sm"
                        >
                          Delete
                        </Button>

                        {/* ── Send Bulk button ── */}
                       <Button
                            variant="success"
                            size="sm"
                            disabled={sendingBulk === message.pk_announce_key}
                            onClick={() => handleSendBulk(message)}
                            title="Send announcement email to all parents"
                          >
                            {sendingBulk === message.pk_announce_key ? (   
                              <><Spinner animation="border" size="sm" /> Sending...</>
                            ) : (
                              'Send'
                            )}
                          </Button>

                        {/* ── View Status button ── */}
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleViewStatus(message)}
                          title="View email delivery status for each parent"
                        >
                          👁 View
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center' }}>No Data</td>
                </tr>
              )}
            </tbody>
          </Table>
        </>
      </div>
    </>
  );
};

export default Announcement;